/*
 * Module for the global elements of the calculator
 *
 */

const axios = require("axios");

const appVersion = 'v' + (require('../../../../package.json').version)

const state = {
   appTitle: "Fee Calculator", //Name of the app - used for app title TODO: convert this to use the en.json (ask trev)
   appSubTitle: "Follow the below steps to calculate your course fees. These fees apply if you’re a current or future domestic or international onshore student studying at one of our Australian campuses. ", //global sub title of the app
   appVersion,
   steps: [{
         id: 1, //identifier of the step
         title: "Select your info", //name of the page
         description: "Choose the criteria relevant to you. All fields are mandatory.", //description of the page
         name: "user-info", //the url path for the step
         component: "FirstStep", //the component related to the step
         enabled: true, //whether the step is able to be routed to
         completed: false //whether the step has been completed
      },
      {
         id: 2,
         title: "Select course and units",
         description: "<p>Type in your course name or code then select your course.</p><p>After choosing your course you can select units and majors, plus any additional options (such as co-majors or minors) in the structure provided.<p/>You can also use the drop-down to directly select your units without selecting a course.",
         name: "course-options",
         component: "SecondStep",
         enabled: false,
         completed: false
      },
      {
         id: 3,
         title: "Summary",
         description: "View your selection summary and the estimated tuition fees.",
         name: "results",
         component: "FourthStep",
         enabled: false,
         completed: false
      }
   ],
   currentId: null, //identifies which step is currently routed to
   chunksUpdate: null,
   chunks: {},
   URLs: null,
   URLsUpdate: null,
   disclaimers: [ //fee disclaimers in html format
      {
         id: 1,
         text: "The fee calculator provides an estimated, indicative fee.  It should only be used as a guide. "
      },
      {
         id: 2,
         text: "Not all enrolment, course and fee type combinations represent available options. Students should refer to their Letter of Offer for specific course and fee related information."
      },
      {
         id: 3,
         text: "The fee calculator assumes completion of the course within a standard course duration at a full time study load, commencing in the first teaching period for the commencement year you have selected."
      },
      {
         id: 4,
         text: "The estimated, indicative fee assumes no fee increases or indexation.  Such fee increases or indexation may occur and will affect the estimate.  In the case of a full fee paying student, this may occur at the start of your second and each subsequent year of study.  In the case of Commonwealth supported places, this may occur for each new calendar year."
      },
      {
         id: 5,
         text: "The estimated, indicative fee: (a) is calculated based on the units selected and their associated courses, and may change if different units or courses are selected; (b) is for tuition fees only, and certain <a href='https://www.murdoch.edu.au/study/fees-scholarships/course-fees'>additional fees and charges will apply</a> (c) is in Australian dollars; and (d)  does not account for any scholarships for which you may be eligible."
      },
      {
         id: 6,
         text: "Commonwealth supported places for domestic students are limited in number, and Murdoch University cannot ensure that any student will be entitled to one of these places."
      },
      {
         id: 7,
         text: "Please check the Murdoch University Handbook to ensure you are selecting the correct Units for your selected course, and for the correct years.<br/>Note that units may be subject to change.  In particular, elective units may change or may be unavailable for a particular year."
      },
   ],
   enrolmentTypes: {
      label: "Enrolment Type",
      info: "<div class=\"p-info\"><b>Domestic Student</b>" +
         "<p>You're considered a domestic student at Murdoch if you are an Australian citizen or permanent resident,a New Zealand citizen, or hold a permanent Australian humanitarian visa.</p></div>" +
         "<div class=\"p-info\"><b>International Student</b>" +
         "<p>You're an international student if you hold or are applying for a Student, Diplomatic, Bridging, Temporary or Provisional Resident visa, or are a permanent resident of New Zealand.</p></div>"
   },
   feeTypes: {
      label: "Fee Type",
      info: "<div class=\"p-info\"><b>Commonwealth Supported</b>" +
         "<p>If you are a Domestic student applying for an Undergraduate course, you may be eligible for a Commonwealth Supported place. For more information on Commonwealth Supported places, please visit the <a href=\"https://www.studyassist.gov.au/help-loans/commonwealth-supported-places-csps\"> Study Assist website.</a></p></div>" +
         "<div class=\"p-info\"><b>Full Fee Paying</b>" +
         "<p>Graduate Coursework programs for Domestic students are full fee paying, unless indicated otherwise on your Letter of Offer.</p>" +
         "<p>International students are full fee paying.</p></div>" +
         "<div class=\"p-info\"><b>Please select the relevant fee type in the Fee Calculator to generate an estimate of tuition fees.</b></div>"

   },
   courseTypes: {
      label: "Course Type",
      info: "<div class=\"p-info\"><b>Undergraduate</b>" +
         "<p>In undergraduate courses you'll learn through a combination of lectures, tutorials and private study, with work placements also available in most courses. If you haven't already completed a university degree, an undergraduate course is a great place to start. When you complete your undergraduate course you will be awarded a bachelor degree.</p></div>" +
         "<div class=\"p-info\"><b>Graduate Coursework</b>" +
         "<p>Postgraduate courses offer a blend of theory and practical work, with a focus on real-world applicability and industry projects or work placements. If you’ve completed a bachelor or higher degree or have relevant professional experience, you can advance to postgraduate study.</p></div>" +
         "<div class=\"p-info\"><b>Research</b>" +
         "<p>Our researchers focus on the significant social and scientific challenges of our time, including climate change, environmental sustainability and adaptation, food, water and biosecurity, as well as human and animal health and welfare. If you have sufficient previous research training experience, you may be able to enroll in a research course.</p></div>"
   },
   commencementYears: {
      label: "Commencement Year",
      info: "<div class=\"p-info\"><b>Commencement Year</b>" +
         "<p>Your commencement year is the year you enrolled/plan on enrolling at Murdoch. If you are admitted to the university and then defer your start, your commencement year will be the year you are admitted rather than the year you did your first unit.</p></div>"
   },
};

const getters = {
   //returns the application title
   getTitle: state => {
      return state.appTitle;
   },
   //returns the application sub title
   getSubTitle: state => {
      return state.appSubTitle;
   },
   getIdFromName: state => name => {
      return state.steps.find(step => step.name === name);
   },
   //returns a single step
   getStep: state => id => {
      if (getters.inRange(id)) {
         return state.steps.find(step => step.id === id);
      }
   },
   //returns all the steps
   getSteps: state => {
      return state.steps;
   },
   //returns the step which is the current step
   getCurrentStep: state => {
      // console.log('Get Step', state.currentId)
      return state.steps.find(step => step.id === state.currentId);
   },
   //returns the number of steps
   getStepsLength: state => {
      return state.steps.length;
   },
   //returns on array of paths
   getPaths: state => {
      return state.steps.map(step => step.path);
   },
   getArrayIndex: state => id => {
      return state.steps.findIndex(x => x.id === id);
   },
   //returns boolean if the given step id is complete
   checkComplete: (state, getters) => id => {
      if (getters.inRange(id)) {
         return state.steps.find(step => step.id === id).completed;
      }
   },
   //returns boolean if the given id is a step
   inRange: (state, getters) => id => {
      return id > 0 && id <= getters.getStepsLength;
   },
   //returns the "default path"
   //the path which the dev chooses to be the path that if no parameter is given
   //it will route to - this is the first page (id of 1)
   getDefaultPath: state => {
      return state.steps.find(step => step.id === 1).path;
   },
   getEnrolmentTypes: state => {
      return state.enrolmentTypes;
   },
   getFeeTypes: state => {
      return state.feeTypes;
   },
   getCourseTypes: state => {
      return state.courseTypes;
   },
   getCommencementYears: state => {
      return state.commencementYears;
   }
};

const mutations = {
   SET_CHUNKS(state, chunks) {
      state.chunks = chunks;
      state.chunksUpdate = true;
   },
   SET_URLs(state, url) {
      state.URLs = url;
      state.URLsUpdate = true;
   },

   //sets the current step
   setCurrent(state, id) {
      state.currentId = id;
   },
   completeStep(state, id) {
      state.steps[id].completed = true;
   },
   enableStep(state, id) {
      state.steps[id].enabled = true;
   },
   incompleteStep(state, id) {
      state.steps[id].completed = false;
   },
   disableStep(state, id) {
      state.steps[id].enabled = false;
   }
};

const actions = {
   fetchChunks({
      state,
      commit,
      rootState,
      rootGetters
   }) {
      if (!state.chunksUpdate) {
         axios.get(BaseURL + "/api/data/chunks").then(response => {
            commit("SET_CHUNKS", response.data);
         });
      }
   },

   fetchURLs({
      state,
      commit,
      rootState,
      rootGetters
   }) {
      if (!state.URLUpdate) {
         axios.get(BaseURL + "/api/data/urls").then(response => {
            commit("SET_URLs", response.data);
         });
      }
   },

   //used by consumer to set the current step
   setCurrent(context, toId) {
      if (context.getters.inRange(toId)) {

         context.commit("setCurrent", toId);
      }
   },
   //Used by consumer to set the current step to complete
   completeStep(context, id) {
      if (context.getters.inRange(id)) {
         context.commit("completeStep", context.getters.getArrayIndex(id));
      }
   },
   enableStep(context, id) {
      if (context.getters.inRange(id)) {
         context.commit("enableStep", context.getters.getArrayIndex(id));
      }
   },
   incompleteStep(context, id) {
      if (context.getters.inRange(id)) {
         context.commit("incompleteStep", context.getters.getArrayIndex(id));
      }
   },
   disableStep(context, id) {
      if (context.getters.inRange(id)) {
         context.commit("disableStep", context.getters.getArrayIndex(id));
      }
   },

};

export default {
   state,
   getters,
   actions,
   mutations
};
